/**
 * Presentation Layer - Scan Screen (Web)
 * 
 * Implementación específica para web del escáner de códigos QR.
 * Usa html5-qrcode para escaneo en navegador.
 */

import React, {useState, useEffect} from 'react';
import {View, StyleSheet, Text} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {ActivityIndicator, useTheme} from 'react-native-paper';
import {ActionButton} from '../../../shared/components/ActionButton';
import {ServiceContainer} from '../../../shared/di/ServiceContainer';
import {Beneficiary} from '../../../domain/scan/entities/Beneficiary';
import {CameraAdapterFactory} from '../../../infrastructure/camera/CameraAdapterFactory';

interface ScanScreenProps {
  navigation: any;
  onScanSuccess: (beneficiary: Beneficiary, qrCode: string) => void;
}

export const ScanScreen: React.FC<ScanScreenProps> = ({
  navigation,
  onScanSuccess,
}) => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [processing, setProcessing] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const theme = useTheme();
  const scanService = ServiceContainer.getInstance().getScanService();
  const cameraAdapter = CameraAdapterFactory.getInstance();

  // Request camera permissions
  useEffect(() => {
    (async () => {
      const {granted} = await cameraAdapter.requestPermissions();
      setHasPermission(granted);
      if (granted) {
        setCameraReady(true);
      }
    })();
  }, []);

  // Reset scanner when screen receives focus
  useFocusEffect(
    React.useCallback(() => {
      setProcessing(false);
      setScanned(false);

      // Start scanning when screen is focused
      if (hasPermission && cameraReady) {
        startScanning();
      }

      return () => {
        // Stop scanning when screen loses focus
        cameraAdapter.stopScanning();
      };
    }, [hasPermission, cameraReady]),
  );

  const startScanning = async () => {
    try {
      await cameraAdapter.startScanning(
        async (result) => {
          if (scanned || processing) return;
          await handleScan(result.data);
        },
        (error) => {
          console.error('Error en escaneo:', error);
        }
      );
    } catch (error) {
      console.error('Error al iniciar escaneo:', error);
    }
  };

  const handleScan = async (data: string) => {
    if (scanned || processing) return;

    setScanned(true);
    setProcessing(true);

    try {
      const beneficiary = await scanService.scanQRCode(data);
      await cameraAdapter.stopScanning();
      setProcessing(false);
      onScanSuccess(beneficiary, data);
    } catch (error: any) {
      setProcessing(false);
      await cameraAdapter.stopScanning();
      navigation.navigate('ScanError', {
        errorMessage: error.message || 'Código QR no válido o no encontrado',
      });
    }
  };

  const handleCancel = async () => {
    await cameraAdapter.stopScanning();
    navigation.goBack();
  };

  // Permission not determined yet
  if (hasPermission === null) {
    return (
      <View style={[styles.container, styles.centerContent, {backgroundColor: theme.colors.background}]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.processingText, {color: theme.colors.onBackground}]}>
          Solicitando permisos de cámara...
        </Text>
      </View>
    );
  }

  // Permission denied
  if (hasPermission === false) {
    return (
      <View style={[styles.container, styles.centerContent, {backgroundColor: theme.colors.background}]}>
        <Text style={[styles.errorText, {color: theme.colors.error}]}>
          📷 No se otorgaron permisos de cámara
        </Text>
        <Text style={[styles.errorSubtext, {color: theme.colors.onBackground}]}>
          Por favor habilita los permisos en tu navegador
        </Text>
        <View style={styles.errorButtonWrapper}>
          <ActionButton
            icon="arrow-back"
            label="Volver"
            variant="contained"
            onPress={handleCancel}
          />
        </View>
      </View>
    );
  }

  // Processing state
  if (processing) {
    return (
      <View style={[styles.container, styles.centerContent, {backgroundColor: theme.colors.background}]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.processingText, {color: theme.colors.onBackground}]}>
          Procesando código QR...
        </Text>
      </View>
    );
  }

  // Camera view
  return (
    <View style={styles.container}>
      {/* Top overlay */}
      <View style={styles.topOverlay}>
        <Text style={styles.title}>Escanear Código QR</Text>
        <Text style={styles.subtitle}>
          Apunta la cámara al código QR del servicio
        </Text>
      </View>

      {/* QR Reader container */}
      <View style={styles.cameraContainer}>
        <div id="qr-reader" style={{width: '100%', maxWidth: 500}}></div>
      </View>

      {/* Bottom button */}
      <View style={styles.buttonContainer}>
        <ActionButton
          icon="close"
          label="Cancelar"
          variant="outlined"
          onPress={handleCancel}
          danger
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  cameraContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  topOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingTop: 60,
    paddingBottom: 20,
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 18,
    color: '#fff',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  buttonContainer: {
    paddingBottom: 40,
    paddingHorizontal: 20,
  },
  errorButtonWrapper: {
    marginTop: 20,
    alignSelf: 'stretch',
  },
  processingText: {
    marginTop: 16,
    fontSize: 20,
  },
  errorText: {
    marginTop: 16,
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
  },
  errorSubtext: {
    marginTop: 8,
    fontSize: 18,
    textAlign: 'center',
    paddingHorizontal: 40,
  },
});
